import { useState, useEffect, useMemo } from 'react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Timeline from '@/components/ui/Timeline'
import HistoryContent from '@/components/ui/HistoryContent'
import RecentArticles from '@/components/sections/RecentArticles'
// import StrategicPillars from '@/components/sections/StrategicPillars'
import Newsletter from '@/components/sections/Newsletter'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { useHistories } from '@/lib/api/hooks/history'
import type { History } from '@/lib/api/types'
import { parseHtmlString } from '@/utils/helpers/html'

const OurHistory = () => {
  // Fetch web page content
  const { data, isFetching, isError, refetch } = useAllWebPages()
  const ourHistoryData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'our_history' ||
    page.websitePageName?.toLowerCase() === 'our history'
  )?.content || null

  // Fetch all histories from backend
  const { data: historiesResponse, isLoading } = useHistories({})

  // Extract unique years and group histories by year
  const { years, historiesByYear } = useMemo(() => {
    const histories = historiesResponse?.data?.data || []

    // Group histories by year
    const grouped: Record<string, History[]> = {}
    const yearSet = new Set<string>()

    histories.forEach((history) => {
      const yearStr = history.year.toString()
      yearSet.add(yearStr)

      if (!grouped[yearStr]) {
        grouped[yearStr] = []
      }
      grouped[yearStr].push(history)
    })

    // Sort years in descending order
    const sortedYears = Array.from(yearSet).sort((a, b) =>
      parseInt(b) - parseInt(a)
    )

    return {
      years: sortedYears,
      historiesByYear: grouped
    }
  }, [historiesResponse])

  // Set active year to the most recent year (first in sorted array)
  const [activeYear, setActiveYear] = useState<string>('')

  // Update activeYear when years change
  useEffect(() => {
    if (years.length > 0 && !activeYear) {
      setActiveYear(years[0])
    }
  }, [years, activeYear])

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Our History', href: '/about/history', active: true }
  ]

  useEffect(() => {
    if (years.length === 0) return

    const handleScroll = () => {
      const sections = years.map(year => document.getElementById(`section-${year}`))
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveYear(years[i])
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [years])


  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!ourHistoryData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }


  return (
    <div className="bg-white min-h-screen">

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8  ">
        <Breadcrumb items={breadcrumbItems} />
      </div>


      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <img
          className="w-full h-[300px] md:h-[480px] object-cover"
          src={ourHistoryData?.OUR_HISTORY_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1756135980/436e364d5664edaf65624f6162f6c339235305d5_1_tlzx6p.png"}
          alt="African Space Agency Building"
        />
      </div>


      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-normal font-pt-serif text-gray-100 leading-tight mb-6">
            {ourHistoryData?.OUR_HISTORY?.title ?? "Our History"}
          </h1>
          <p className="text-lg font-normal font-satoshi text-gray-100 leading-relaxed max-w-3xl">
            <span dangerouslySetInnerHTML={parseHtmlString(ourHistoryData?.OUR_HISTORY?.description ?? "The African Space Agency traces its roots to the adoption of the African Space Policy and Strategy, guiding Africa's path toward a united and advanced space sector.")} />
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

          {years.length > 0 && (
            <div className="hidden lg:block lg:col-span-3 xl:col-span-2">
              <div className="sticky top-14">
                <Timeline
                  years={years}
                  activeYear={activeYear}
                  onYearChange={setActiveYear}
                />
              </div>
            </div>
          )}


          <div className="lg:col-span-6 xl:col-span-7">
            {/* Mobile Timeline - Shown only on mobile */}
            {years.length > 0 && (
              <div className="lg:hidden mb-8">
                <Timeline
                  years={years}
                  activeYear={activeYear}
                  onYearChange={setActiveYear}
                />
              </div>
            )}

            <HistoryContent
              historiesByYear={historiesByYear}
              isLoading={isLoading}
            />
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-3 space-y-10 mt-8 lg:mt-0">
            <div className="lg:sticky lg:top-8 space-y-10">
              {/* <StrategicPillars /> */}
                <RecentArticles />
              <Newsletter />
            </div>

       
          </div>
        </div>
      </div>
    </div>
  )
}

export default OurHistory